import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Badge, Modal, Tab, Tabs } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { 
  Person, 
  Envelope, 
  Phone, 
  GeoAlt, 
  Calendar, 
  Shield, 
  ShieldFill,
  Key, 
  Bell, 
  Globe, 
  Save, 
  Pencil, 
  Camera, 
  CheckCircle, 
  XCircle,
  ArrowClockwise,
  Lock,
  Eye,
  EyeSlash
} from 'react-bootstrap-icons';
import { useAccount } from '../contexts/AccountContext';
import { useUser } from '../contexts/UserContext';
import { useTheme } from '../contexts/ThemeContext';
import { UserRole, ROLE_PERMISSIONS, getRoleConfig } from '../types/userRoles';
import { useUpdateUserMutation, useGetUserLoginHistoryQuery, useGetUserMFAStatusQuery, useInitiateMFAEnrollmentMutation, useChangePasswordMutation, useGetUsersQuery, useUploadUserAvatarMutation, useGetUserPreferencesQuery } from '../services/api';
import { useDispatch } from 'react-redux';
import EMMSectionHeader from './EMMSectionHeader';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
  toLocaleStringInTimezone,
} from '../utils/tenantTimezone';
import { useUserPreferencesQueryArg } from '../hooks/useTenantIdForPreferences';

const Profile = () => {
  const { selectedAccount, isAllAccountsSelected, isLoading } = useAccount();
  const { currentUser, updateUser: updateUserContext } = useUser();
  const { theme, setTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [updateUserApi, { isLoading: isSaving }] = useUpdateUserMutation();
  const [changePasswordApi, { isLoading: isChangingPassword }] = useChangePasswordMutation();
  
  // Track whether we've received fresh MFA data from API (prevents showing stale cache)
  // Also track if we've cleared cache and are ready to fetch
  // IMPORTANT: Declare these BEFORE the query hooks so they can be used in skip condition
  // CRITICAL: Start with false to prevent showing cached data on initial sign-in
  const [hasReceivedFreshMFAData, setHasReceivedFreshMFAData] = useState(false);
  const [isReadyToFetchMFA, setIsReadyToFetchMFA] = useState(false);
  const [cacheCleared, setCacheCleared] = useState(false); // Track if cache has been cleared for this session
  
  // Fetch login history for current user
  const { data: loginHistoryData, isLoading: isLoadingHistory } = useGetUserLoginHistoryQuery(
    currentUser?.id, 
    { skip: !currentUser?.id }
  );
  
  // Fetch MFA status for current user
  // CRITICAL: Skip query until cache is confirmed cleared AND ready flag is set
  // This prevents showing stale cached data on initial sign-in
  const { data: mfaStatusData, isLoading: isLoadingMFA, isFetching: isFetchingMFA, refetch: refetchMFA } = useGetUserMFAStatusQuery(
    currentUser?.id,
    { 
      skip: !currentUser?.id || !isReadyToFetchMFA || !cacheCleared, // Skip until cache cleared AND ready
      refetchOnMountOrArgChange: true, // Force fresh data on component mount
      refetchOnFocus: false, // Don't refetch on window focus
      refetchOnReconnect: false // Don't refetch on reconnect
    }
  );
  
  // Fetch users to get password_last_changed
  const { data: usersData, refetch: refetchUsers } = useGetUsersQuery(undefined, { skip: !currentUser?.id });
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );
  const currentUserData = usersData?.data?.users?.find(u => u.id === currentUser?.id);
  const passwordLastChanged = currentUserData?.passwordLastChanged || null;
  
  const [initiateMFAEnrollment, { isLoading: isEnrollingMFA }] = useInitiateMFAEnrollmentMutation();
  
  // Reset ALL flags when user changes (critical for initial sign-in)
  useEffect(() => {
    console.log('🔄 User changed - resetting all MFA flags', { userId: currentUser?.id });
    setHasReceivedFreshMFAData(false);
    setIsReadyToFetchMFA(false);
    setCacheCleared(false);
  }, [currentUser?.id]);

  // Only consider MFA enabled if we have fresh data AND it's explicitly true
  // Default to false when loading, fetching, or when data is undefined to prevent showing "Enabled" incorrectly
  // Wait for query to complete (not loading AND not fetching) before evaluating status
  const isMFAQueryActive = isLoadingMFA || isFetchingMFA;
  
  // Track when we receive fresh data (after query completes)
  // IMPORTANT: Only mark as fresh when query has COMPLETED (not loading, not fetching)
  // AND we have valid data with a defined mfaEnabled value
  useEffect(() => {
    // Only accept data as "fresh" if:
    // 1. Query is NOT active (not loading, not fetching)
    // 2. We have data
    // 3. The data has a valid mfaEnabled field (not undefined)
    if (!isMFAQueryActive && mfaStatusData && mfaStatusData?.data !== undefined && typeof mfaStatusData.data.mfaEnabled === 'boolean') {
      console.log('🔍 MFA Status Data (FRESH):', {
        data: mfaStatusData,
        mfaEnabled: mfaStatusData?.data?.mfaEnabled,
        isLoading: isLoadingMFA,
        isFetching: isFetchingMFA,
        isActive: isMFAQueryActive,
        previousFreshDataFlag: hasReceivedFreshMFAData
      });
      
      // Mark that we've received fresh data from the API
      // Only do this once to prevent multiple updates
      if (!hasReceivedFreshMFAData) {
        console.log('✅ First fresh MFA data received - mfaEnabled:', mfaStatusData?.data?.mfaEnabled);
        setHasReceivedFreshMFAData(true);
      }
    } else if (isMFAQueryActive && hasReceivedFreshMFAData) {
      // If query becomes active again (refetch), reset the flag
      // This prevents showing stale data during refetch
      console.log('🔄 MFA query active again - resetting fresh data flag');
      setHasReceivedFreshMFAData(false);
    }
  }, [mfaStatusData, isLoadingMFA, isFetchingMFA, isMFAQueryActive, hasReceivedFreshMFAData]);
  
  // Only evaluate MFA status when query is complete AND we have confirmed fresh data
  // Treat undefined/null/false all as disabled
  // IMPORTANT: Only show status after we've confirmed fresh data has been received
  const mfaEnabled = !isMFAQueryActive && hasReceivedFreshMFAData && mfaStatusData && mfaStatusData?.data?.mfaEnabled === true;
  
  // Clear cache and enable query on mount
  // CRITICAL: This MUST run on initial sign-in to prevent showing stale cached data
  // This runs once when the component mounts or user ID changes
  useEffect(() => {
    if (currentUser?.id && !cacheCleared) {
      console.log('🔍 Profile mount - Clearing MFA cache before enabling query', {
        userId: currentUser.id,
        isReadyToFetch: isReadyToFetchMFA,
        cacheCleared: cacheCleared,
        hasCurrentUser: !!currentUser
      });
      
      // Reset ALL flags first to prevent any race conditions
      setHasReceivedFreshMFAData(false);
      setIsReadyToFetchMFA(false);
      
      // Invalidate the cache entry FIRST before enabling query
      // This ensures we don't use stale cached data on initial sign-in
      import('../services/api').then(({ marketingApi }) => {
        console.log('🧹 Starting cache clear...');
        
        // Invalidate all user-related cache
        dispatch(marketingApi.util.invalidateTags([{ type: 'Users', id: currentUser.id }]));
        
        // Also reset the entire API state to clear all cache
        // This is aggressive but ensures no stale data on initial sign-in
        dispatch(marketingApi.util.resetApiState());
        
        // Also explicitly remove the cached entry for this specific query
        dispatch(marketingApi.util.invalidateTags([{ type: 'Users' }]));
        
        console.log('✅ Cache cleared - marking cache as cleared');
        
        // Mark cache as cleared FIRST
        setCacheCleared(true);
        
        // Now enable the query AFTER cache is confirmed cleared
        // Use a delay to ensure cache clear completes first
        setTimeout(() => {
          console.log('🚀 Enabling MFA query now...');
          setIsReadyToFetchMFA(true);
        }, 150); // Slightly longer delay to ensure cache clear completes
      }).catch((err) => {
        console.error('❌ Error clearing cache:', err);
        // Even if cache clear fails, mark as cleared and enable query
        setCacheCleared(true);
        setTimeout(() => setIsReadyToFetchMFA(true), 150);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, cacheCleared]); // Run when user ID changes OR when cache cleared flag changes
  
  // Additional profile data that's not in currentUser
  const additionalProfileData = {
    security: {
      twoFactorEnabled: false, // This should come from API, not hardcoded
      lastPasswordChange: '2024-01-15',
      loginHistory: [
        { date: '2024-01-20T10:30:00Z', location: 'New York, NY', device: 'Chrome on MacOS', ip: '192.168.1.100' },
        { date: '2024-01-19T14:22:00Z', location: 'New York, NY', device: 'Chrome on MacOS', ip: '192.168.1.100' },
        { date: '2024-01-18T09:15:00Z', location: 'New York, NY', device: 'Safari on iPhone', ip: '192.168.1.101' }
      ],
      apiKeys: [
        { name: 'Marketing API Key', created: '2024-01-10', lastUsed: '2024-01-20T10:30:00Z', status: 'active' },
        { name: 'Analytics API Key', created: '2024-01-05', lastUsed: '2024-01-19T14:22:00Z', status: 'active' }
      ]
    },
    preferences: {
      notifications: {
        email: true,
        push: true,
        sms: false,
        campaignUpdates: true,
        systemAlerts: true,
        weeklyReports: true
      },
      display: {
        theme: 'light',
        language: 'en',
        timezone: 'America/New_York',
        dateFormat: 'MM/DD/YYYY',
        currency: 'USD'
      },
      privacy: {
        profileVisibility: 'team',
        showEmail: true,
        showPhone: false,
        dataSharing: false
      }
    },
    // Activity data removed - using real data from currentUser context instead
  };

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'personal');
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [preferences, setPreferences] = useState(additionalProfileData.preferences);
  const [uploadUserAvatar, { isLoading: isUploadingAvatar }] = useUploadUserAvatarMutation();
  // Use isLoading from the mutation hook for saving state
  const [showPassword, setShowPassword] = useState(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState(null);

  // Ensure user object always has all required properties to prevent undefined errors
  const user = currentUser ? {
    ...currentUser,
    firstName: currentUser.firstName || '',
    lastName: currentUser.lastName || '',
    title: currentUser.title || '',
    email: currentUser.email || '',
    location: currentUser.location || '',
    role: currentUser.role || 'viewer',
    phone: currentUser.phone || '',
    department: currentUser.department || '',
    timezone: currentUser.timezone || 'America/Los_Angeles',
    bio: currentUser.bio || '',
    avatar: currentUser.avatar || null,
    createdAt: currentUser.createdAt || new Date().toISOString()
  } : {
    firstName: '',
    lastName: '',
    title: '',
    email: '',
    location: '',
    role: 'viewer',
    phone: '',
    department: '',
    timezone: 'America/Los_Angeles',
    bio: '',
    avatar: null,
    createdAt: new Date().toISOString()
  };

  // Watch for URL parameter changes to update active tab
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleTabSelect = (key) => {
    if (!key) return;
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleSave = async () => {
    if (!currentUser?.id) {
      alert('User ID not found. Please try refreshing the page.');
      return;
    }

    try {
      // Prepare profile data to save
      const profileData = {
        userId: currentUser.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        title: user.title,
        department: user.department,
        location: user.location,
        timezone: user.timezone,
        bio: user.bio
      };

      console.log('🔍 Profile save - Sending data:', profileData);

      // Call the API to update user profile
      // Note: invalidatesTags: ['Users'] will automatically refetch getUsers query
      const result = await updateUserApi(profileData).unwrap();
      
      console.log('🔍 Profile save - API response:', result);

      // Update local context with saved data from API response
      // RTK Query will automatically refetch Users query due to invalidatesTags
      if (result.data?.user) {
        updateUserContext({
          firstName: result.data.user.firstName || user.firstName,
          lastName: result.data.user.lastName || user.lastName,
          email: result.data.user.email || user.email,
          phone: result.data.user.phone || user.phone,
          title: result.data.user.title || user.title,
          department: result.data.user.department || user.department,
          location: result.data.user.location || user.location,
          timezone: result.data.user.timezone || user.timezone,
          bio: result.data.user.bio || user.bio,
          profileUpdatedAt: result.data.user.profileUpdatedAt
        });
      }

      setIsEditing(false);
      // TODO: Show success message
    } catch (error) {
      console.error('Failed to save profile:', error);
      alert(`Failed to save profile: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handlePasswordChange = async () => {
    // Clear previous messages
    setPasswordChangeSuccess(false);
    setPasswordChangeError(null);
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordChangeError('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      setPasswordChangeError('New password must be at least 8 characters');
      return;
    }
    
    if (!currentUser?.id) {
      setPasswordChangeError('User ID not found. Please try refreshing the page.');
      return;
    }

    try {
      console.log('🔍 Changing password for user:', currentUser.id);
      
      // Call the API to change password
      const result = await changePasswordApi({
        userId: currentUser.id,
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      }).unwrap();
      
      console.log('✅ Password change result:', result);
      
      // Show success message
      setPasswordChangeSuccess(true);
      setPasswordChangeError(null);
      
      // Explicitly refetch users to get updated password_last_changed
      await refetchUsers();
      
      // Wait a moment to show success message, then close modal and reset form
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordChangeSuccess(false);
        setPasswordChangeError(null);
      }, 2000); // Show success message for 2 seconds
      
    } catch (error) {
      console.error('❌ Failed to change password:', error);
      const errorMessage = error?.data?.message || error?.data?.error || error?.message || 'Unknown error';
      setPasswordChangeError(errorMessage);
      setPasswordChangeSuccess(false);
    }
  };

  const handleEnableMFA = async () => {
    if (!currentUser?.id) {
      alert('User ID not found. Please try refreshing the page.');
      return;
    }

    try {
      // Initiate MFA enrollment
      const result = await initiateMFAEnrollment(currentUser.id).unwrap();
      
      // If there's an enrollment URL, we could redirect the user
      // For now, Auth0 will handle the enrollment through Universal Login
      if (result.data?.enrollmentUrl) {
        // Option 1: Redirect to Auth0 enrollment page
        window.location.href = result.data.enrollmentUrl;
      } else {
        // Option 2: Show instructions or redirect to Auth0 settings
        alert('MFA enrollment initiated. Please complete the setup by logging in again and following the MFA setup prompts, or visit your Auth0 account settings.');
      }
      
      // Refetch MFA status after enrollment
      await refetchMFA();
    } catch (error) {
      console.error('Failed to enable MFA:', error);
      alert(`Failed to enable MFA: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const formatDate = (dateString) => {
    return toLocaleStringInTimezone(dateString, undefined, undefined, tenantTimezone);
  };

  // Format creation date nicely (e.g., "January 15, 2024")
  const formatCreationDate = (isoDate) => {
    if (!isoDate) return 'Unknown date';
    // Format as "Month Day, Year" (e.g., "January 15, 2024")
    return toLocaleDateStringInTimezone(isoDate, 'en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }, tenantTimezone);
  };

  const getMemberSinceLabel = (isoDate) => {
    if (!isoDate) return 'unknown';
    const start = new Date(isoDate);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    
    // Calculate days using time difference for accuracy
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // If less than 30 days, show days (minimum 1 day if created today)
    if (diffDays < 30) {
      const daysToShow = Math.max(1, diffDays);
      return `${daysToShow} day${daysToShow === 1 ? '' : 's'}`;
    }
    
    // Calculate months accurately
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) {
      return `${diffMonths} month${diffMonths === 1 ? '' : 's'}`;
    }
    
    // Calculate years
    const diffYears = Math.floor(diffDays / 365);
    return `${diffYears} year${diffYears === 1 ? '' : 's'}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge bg="success"><CheckCircle className="me-1" />Active</Badge>;
      case 'inactive':
        return <Badge bg="secondary"><XCircle className="me-1" />Inactive</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Guard against undefined currentUser - this can happen during refetches
  // Also check that user object has required properties
  if (!currentUser || !user || !user.firstName) {
    return (
      <Container className="py-4">
        <div className="text-center text-muted">
          <ArrowClockwise size={24} className="text-primary mb-2" style={{ animation: 'spin 1s linear infinite' }} />
          <div>Loading profile...</div>
        </div>
      </Container>
    );
  }

  return (
    <>
      <EMMSectionHeader title="Profile" showHelp />
      <Container className="py-3" style={{ position: 'relative' }}>
      {/* Loading Overlay */}
      {isLoading && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}
        >
          <div className="text-center">
            <ArrowClockwise size={32} className="text-primary mb-2" style={{ animation: 'spin 1s linear infinite' }} />
            <div className="text-muted">Switching to {selectedAccount.alias}...</div>
          </div>
        </div>
      )}

      {/* Profile Header */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <Row className="align-items-center">
                <Col md="auto">
                  <div className="position-relative">
                    {(currentUser?.avatar || currentUserData?.avatar) ? (
                      <img 
                        src={currentUser?.avatar || currentUserData?.avatar} 
                        alt={`${user.firstName} ${user.lastName}`}
                        className="rounded-circle"
                        style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div 
                        className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white"
                        style={{ width: '120px', height: '120px', fontSize: '48px' }}
                      >
                        <Person size={60} />
                      </div>
                    )}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="position-absolute bottom-0 end-0 rounded-circle"
                      style={{ width: '32px', height: '32px' }}
                      onClick={() => {
                        setAvatarPreview(currentUser?.avatar || currentUserData?.avatar || null);
                        setAvatarFile(null);
                        setShowAvatarModal(true);
                      }}
                      title="Change Profile Picture"
                    >
                      <Camera size={16} />
                    </Button>
                  </div>
                </Col>
                <Col>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h2 className="h4 mb-1">
                        {user.firstName} {user.lastName}
                      </h2>
                      <p className="text-muted mb-2">{user.title}</p>
                      <div className="mb-2">
                        <Badge 
                          bg={getRoleConfig(user.role)?.color || 'secondary'}
                          className="d-flex align-items-center w-auto"
                        >
                          <Shield className="me-1" />
                          {getRoleConfig(user.role)?.name || 'Unknown Role'}
                        </Badge>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <Envelope className="me-2 text-muted" size={16} />
                        <span>{user.email}</span>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <GeoAlt className="me-2 text-muted" size={16} />
                        <span>{user.location}</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <Calendar className="me-2 text-muted" size={16} />
                        <span>Member since {formatCreationDate(user.createdAt)}</span>
                      </div>
                    </div>
                    <div className="text-end">
                      <Button 
                        variant={isEditing ? "success" : "outline-primary"}
                        onClick={() => setIsEditing(!isEditing)}
                        className="mb-2"
                      >
                        {isEditing ? <Save className="me-1" /> : <Pencil className="me-1" />}
                        {isEditing ? 'Save Changes' : 'Edit Profile'}
                      </Button>
                      {user.profileUpdatedAt && (
                        <div className="text-muted small mt-2">
                          Last updated: {formatDate(user.profileUpdatedAt)}
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Profile Tabs */}
      <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="mb-4">
        <Tab eventKey="personal" title={
          <span><Person className="me-1" />Personal Information</span>
        }>
          <Row>
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Personal Details</h5>
                </Card.Header>
                <Card.Body>
                  <Form>
                    {/* Profile Picture Upload Section - Only show when editing */}
                    {isEditing && (
                      <Row className="mb-4">
                        <Col>
                          <Form.Group>
                            <Form.Label>Profile Picture</Form.Label>
                            <div className="d-flex align-items-center">
                              <div className="me-3">
                                {(currentUser?.avatar || currentUserData?.avatar) ? (
                                  <img 
                                    src={currentUser?.avatar || currentUserData?.avatar} 
                                    alt={`${user.firstName} ${user.lastName}`}
                                    className="rounded-circle"
                                    style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div 
                                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                                    style={{ width: '80px', height: '80px', fontSize: '32px', fontWeight: 'bold' }}
                                  >
                                    {user.firstName?.charAt(0) || 'U'}
                                  </div>
                                )}
                              </div>
                              <div className="flex-grow-1">
                                <Button
                                  variant="outline-primary"
                                  onClick={() => {
                                    setAvatarPreview(currentUser?.avatar || currentUserData?.avatar || null);
                                    setAvatarFile(null);
                                    setShowAvatarModal(true);
                                  }}
                                >
                                  <Camera className="me-2" />
                                  {(currentUser?.avatar || currentUserData?.avatar) ? 'Change Picture' : 'Upload Picture'}
                                </Button>
                                <Form.Text className="d-block text-muted mt-2">
                                  Recommended: Square image, max 5MB. Formats: JPG, PNG, GIF
                                </Form.Text>
                              </div>
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name</Form.Label>
                          <Form.Control
                            type="text"
                          value={user.firstName}
                            onChange={(e) => updateUserContext({ firstName: e.target.value })}
                            disabled={!isEditing}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name</Form.Label>
                          <Form.Control
                            type="text"
                          value={user.lastName}
                            onChange={(e) => updateUserContext({ lastName: e.target.value })}
                            disabled={!isEditing}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                          value={user.email}
                            onChange={(e) => updateUserContext({ email: e.target.value })}
                            disabled={!isEditing}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            type="tel"
                          value={user.phone}
                            onChange={(e) => updateUserContext({ phone: e.target.value })}
                            disabled={!isEditing}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Job Title</Form.Label>
                          <Form.Control
                            type="text"
                          value={user.title}
                            onChange={(e) => updateUserContext({ title: e.target.value })}
                            disabled={!isEditing}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Department</Form.Label>
                          <Form.Control
                            type="text"
                          value={user.department}
                            onChange={(e) => updateUserContext({ department: e.target.value })}
                            disabled={!isEditing}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Location</Form.Label>
                          <Form.Control
                            type="text"
                          value={user.location}
                            onChange={(e) => updateUserContext({ location: e.target.value })}
                            disabled={!isEditing}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Timezone</Form.Label>
                          <Form.Select
                            value={user.timezone}
                            onChange={(e) => updateUserContext({ timezone: e.target.value })}
                            disabled={!isEditing}
                          >
                            <option value="America/New_York">Eastern Time</option>
                            <option value="America/Chicago">Central Time</option>
                            <option value="America/Denver">Mountain Time</option>
                            <option value="America/Los_Angeles">Pacific Time</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label>Bio</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={user.bio}
                        onChange={(e) => updateUserContext({ bio: e.target.value })}
                        disabled={!isEditing}
                      />
                    </Form.Group>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="security" title={
          <span><Shield className="me-1" />Security</span>
        }>
          <Row>
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Account Security</h5>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Two-Factor Authentication</span>
                      {/* CRITICAL: Show Loading if:
                          1. Cache hasn't been cleared yet (initial sign-in protection)
                          2. Query isn't ready yet
                          3. Query is active (loading/fetching)
                          4. We don't have confirmed fresh data
                      */}
                      {!cacheCleared || !isReadyToFetchMFA || isMFAQueryActive || !hasReceivedFreshMFAData || !mfaStatusData || mfaStatusData?.data === undefined ? (
                        <Badge bg="secondary">Loading...</Badge>
                      ) : (
                        <Badge bg={mfaEnabled ? 'success' : 'warning'}>
                          {mfaEnabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      )}
                    </div>
                    {/* Only show enable button if query is complete, we have confirmed fresh data, and MFA is disabled */}
                    {!isMFAQueryActive && hasReceivedFreshMFAData && mfaStatusData && mfaStatusData?.data !== undefined && !mfaEnabled && (
                      <div>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={handleEnableMFA}
                          disabled={isEnrollingMFA}
                        >
                          {isEnrollingMFA ? (
                            <>
                              <ArrowClockwise className="me-1" style={{ animation: 'spin 1s linear infinite' }} />
                              Setting up...
                            </>
                          ) : (
                            <>
                              <Shield className="me-1" />
                              Enable 2FA
                            </>
                          )}
                        </Button>
                        <small className="d-block text-muted mt-1">
                          Protect your account with two-factor authentication
                        </small>
                      </div>
                    )}
                    {/* Only show enabled message if query is complete, we have confirmed fresh data, and MFA is explicitly enabled */}
                    {!isMFAQueryActive && hasReceivedFreshMFAData && mfaStatusData && mfaEnabled && (
                      <small className="text-muted d-block">
                        Your account is protected with two-factor authentication
                      </small>
                    )}
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Password</span>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => setShowPasswordModal(true)}
                      >
                        <Key className="me-1" />
                        Change Password
                      </Button>
                    </div>
                    {passwordLastChanged ? (
                      <small className="text-muted">
                        Last changed: {formatDate(passwordLastChanged)}
                      </small>
                    ) : (
                      <small className="text-muted">
                        Password has not been changed
                      </small>
                    )}
                  </div>
                  <div className="mb-3">
                    <h6>Recent Login Activity</h6>
                    {isLoadingHistory ? (
                      <div className="text-muted">Loading login history...</div>
                    ) : loginHistoryData?.data?.loginHistory && loginHistoryData.data.loginHistory.length > 0 ? (
                      loginHistoryData.data.loginHistory.slice(0, 3).map((login, index) => (
                        <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded">
                          <div>
                            <div className="fw-medium">{login.device}</div>
                            <small className="text-muted">{login.location || 'Unknown location'} • {login.ip}</small>
                          </div>
                          <small className="text-muted">{formatDate(login.date)}</small>
                        </div>
                      ))
                    ) : user.lastLogin ? (
                      // Fallback: Show last login if history is not available
                      <div className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded">
                        <div>
                          <div className="fw-medium">Most Recent Login</div>
                          <small className="text-muted">IP address not available</small>
                        </div>
                        <small className="text-muted">{formatDate(user.lastLogin)}</small>
                      </div>
                    ) : (
                      <div className="text-muted">No login history available</div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">API Keys</h5>
                </Card.Header>
                <Card.Body>
                  {additionalProfileData.security.apiKeys.map((key, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center mb-3 p-3 border rounded">
                      <div>
                        <div className="fw-medium">{key.name}</div>
                        <small className="text-muted">
                          Created: {formatDate(key.created)} • Last used: {formatDate(key.lastUsed)}
                        </small>
                      </div>
                      <div className="text-end">
                        {getStatusBadge(key.status)}
                        <Button variant="outline-secondary" size="sm" className="ms-2">
                          <Eye className="me-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline-primary" size="sm">
                    <Key className="me-1" />
                    Generate New Key
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="preferences" title={
          <span><Bell className="me-1" />Preferences</span>
        }>
          <Row>
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Notifications</h5>
                </Card.Header>
                <Card.Body>
                  {Object.entries(additionalProfileData.preferences.notifications).map(([key, value]) => (
                    <div key={key} className="d-flex justify-content-between align-items-center mb-3">
                      <span className="text-capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                      <Form.Check
                        type="switch"
                        checked={value}
                        onChange={(e) => setPreferences({
                          ...preferences,
                          notifications: {
                            ...preferences.notifications,
                            [key]: e.target.checked
                          }
                        })}
                      />
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Display Settings</h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Theme</Form.Label>
                    <Form.Select
                      value={theme}
                      onChange={(e) => {
                        setTheme(e.target.value);
                        setPreferences({
                          ...preferences,
                          display: {
                            ...preferences.display,
                            theme: e.target.value
                          }
                        });
                      }}
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="auto">Auto</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Language</Form.Label>
                    <Form.Select
                      value={preferences.display.language}
                      onChange={(e) => setPreferences({
                        ...preferences,
                        display: {
                          ...preferences.display,
                          language: e.target.value
                        }
                      })}
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Date Format</Form.Label>
                    <Form.Select
                      value={preferences.display.dateFormat}
                      onChange={(e) => setPreferences({
                        ...preferences,
                        display: {
                          ...preferences.display,
                          dateFormat: e.target.value
                        }
                      })}
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </Form.Select>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="activity" title={
          <span><Globe className="me-1" />Activity</span>
        }>
          <Row>
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Account Activity</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    {/* Only show cards with real data - campaigns and emails will show when API is ready */}
                    {currentUser?.createdAt && (
                      <Col md={3}>
                        <div className="text-center p-3 border rounded">
                          <h4 className="text-info">
                            {getMemberSinceLabel(currentUser.createdAt)}
                          </h4>
                          <small className="text-muted">Member Since</small>
                        </div>
                      </Col>
                    )}
                    {currentUser?.lastLogin && (
                      <Col md={3}>
                        <div className="text-center p-3 border rounded">
                          <h4 className="text-warning">
                            {currentUser.lastLogin ? formatDate(currentUser.lastLogin) : 'Never'}
                          </h4>
                          <small className="text-muted">Last Login</small>
                        </div>
                      </Col>
                    )}
                    {/* Hide campaigns and emails until API is available */}
                    {false && (
                      <>
                        <Col md={3}>
                          <div className="text-center p-3 border rounded">
                            <h4 className="text-primary">0</h4>
                            <small className="text-muted">Total Campaigns</small>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center p-3 border rounded">
                            <h4 className="text-success">0</h4>
                            <small className="text-muted">Emails Sent</small>
                          </div>
                        </Col>
                      </>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>
      </Tabs>

      {/* Save Button */}
      {isEditing && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <Button 
            variant="primary" 
            size="lg"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <ArrowClockwise className="me-2" style={{ animation: 'spin 1s linear infinite' }} />
                Saving...
              </>
            ) : (
              <>
                <Save className="me-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      )}

      {/* Change Password Modal */}
      <Modal show={showPasswordModal} onHide={() => {
        setShowPasswordModal(false);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordChangeSuccess(false);
        setPasswordChangeError(null);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {passwordChangeSuccess && (
            <Alert variant="success" className="mb-3">
              <CheckCircle className="me-2" />
              Password updated successfully!
            </Alert>
          )}
          {passwordChangeError && (
            <Alert variant="danger" className="mb-3">
              <XCircle className="me-2" />
              {passwordChangeError}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Current Password</Form.Label>
              <div className="position-relative">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  value={passwordForm.currentPassword}
                  onChange={(e) => {
                    setPasswordForm({ ...passwordForm, currentPassword: e.target.value });
                    setPasswordChangeError(null); // Clear error when user types
                  }}
                  disabled={isChangingPassword || passwordChangeSuccess}
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="position-absolute end-0 top-50 translate-middle-y me-2"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isChangingPassword || passwordChangeSuccess}
                >
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </Button>
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => {
                  setPasswordForm({ ...passwordForm, newPassword: e.target.value });
                  setPasswordChangeError(null); // Clear error when user types
                }}
                disabled={isChangingPassword || passwordChangeSuccess}
              />
              <Form.Text className="text-muted">
                Must be at least 8 characters
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => {
                  setPasswordForm({ ...passwordForm, confirmPassword: e.target.value });
                  setPasswordChangeError(null); // Clear error when user types
                }}
                disabled={isChangingPassword || passwordChangeSuccess}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowPasswordModal(false);
              setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
              setPasswordChangeSuccess(false);
              setPasswordChangeError(null);
            }}
            disabled={isChangingPassword}
          >
            {passwordChangeSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!passwordChangeSuccess && (
            <Button 
              variant="primary" 
              onClick={handlePasswordChange}
              disabled={isChangingPassword}
            >
              {isChangingPassword ? (
                <>
                  <ArrowClockwise className="me-1" style={{ animation: 'spin 1s linear infinite' }} />
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Avatar Upload Modal */}
      <Modal show={showAvatarModal} onHide={() => {
        setShowAvatarModal(false);
        setAvatarPreview(null);
        setAvatarFile(null);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Update Profile Picture</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            {avatarPreview ? (
              <img 
                src={avatarPreview} 
                alt="Profile preview"
                className="rounded-circle mx-auto mb-3"
                style={{ width: '100px', height: '100px', objectFit: 'cover' }}
              />
            ) : (
              <div 
                className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white mx-auto mb-3"
                style={{ width: '100px', height: '100px', fontSize: '40px' }}
              >
                <Person size={50} />
              </div>
            )}
            <p className="text-muted">Upload a new profile picture</p>
            <Form.Group>
              <Form.Control 
                type="file" 
                accept="image/*" 
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // Validate file type
                    if (!file.type.startsWith('image/')) {
                      alert('Please select an image file');
                      return;
                    }
                    
                    // Validate file size (max 5MB)
                    if (file.size > 5 * 1024 * 1024) {
                      alert('Image file size must be less than 5MB');
                      return;
                    }
                    
                    // Store the file for upload
                    setAvatarFile(file);
                    
                    // Create preview
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setAvatarPreview(reader.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <Form.Text className="text-muted">
                Recommended: Square image, max 5MB. Formats: JPG, PNG, GIF
              </Form.Text>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowAvatarModal(false);
            setAvatarPreview(null);
            setAvatarFile(null);
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={async () => {
              if (!avatarFile || !currentUser?.id) {
                alert('Please select an image file to upload');
                return;
              }
              
              try {
                console.log('Uploading avatar for user:', currentUser.id);
                const result = await uploadUserAvatar({
                  userId: currentUser.id,
                  file: avatarFile
                }).unwrap();
                
                console.log('Avatar upload successful:', result);
                
                // Close modal and reset state FIRST (before refreshing to avoid UI issues)
                setShowAvatarModal(false);
                setAvatarPreview(null);
                setAvatarFile(null);
                
                // The mutation already invalidates the ['Users'] tag, which will automatically
                // trigger a refetch of the users query. We don't need to call refetchUsers()
                // explicitly, as it can cause race conditions where currentUser becomes undefined.
                // Just wait a moment for the cache to update naturally.
                
                // Show success message
                alert('Profile picture updated successfully!');
                
                // Small delay to allow cache to update before showing success
                // The component will re-render automatically when the cache updates
              } catch (error) {
                console.error('Error uploading avatar:', error);
                const errorMessage = error?.data?.message || error?.message || 'Failed to upload avatar. Please try again.';
                alert(`Error: ${errorMessage}`);
              }
            }}
            disabled={!avatarFile || isUploadingAvatar}
          >
            {isUploadingAvatar ? (
              <>
                <ArrowClockwise className="me-1" style={{ animation: 'spin 1s linear infinite' }} />
                Uploading...
              </>
            ) : (
              'Update Picture'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </>
  );
};

export default Profile;
